/**
 * Field-by-field comparison of what an export WROTE (the IR) with what reading the bytes back gives, for the strict
 * export self-check (W1-07b formats-package; DESIGN.md §5.11 step 4, §7.1 L1 comparator; CR-W1-07a-7).
 *
 * The decoded side is the RpeChart BEFORE normalisation, so nothing the lenient importer repairs can hide a
 * difference. Rules:
 *  - objects key for key: an explicit `null` differs from an absent key; passthrough keys in order (the writer drops
 *    duplicates, keeping the last occurrence at its own position, and never lets a passthrough key override a
 *    known one, so the expected list is filtered the same way);
 *  - integers and f64 fields compared exactly; f32 fields through `Math.fround` (the writer writes the shortest
 *    decimal that reads back as the same f32, which is what Phira reads); beat triples element by element exactly
 *    (raw, non-normalised triples included); RPEVersion lexemes as text;
 *  - fields the writer always writes (event easing fields, line Group / bpmfactor / anchor / isGif / numOfNotes /
 *    father / rotateWithFather / zOrder, META strings) must be present in the file;
 *  - the integer typing of the file (no `1.0` where Phira wants an integer) is checked on the lossless JSON tree.
 * Differences are collected as "path: expected X, found Y" lines (bounded); the count is kept.
 */

import type {
  IrBpm,
  IrChart,
  IrControlPoint,
  IrEffect,
  IrEvent,
  IrExtended,
  IrExtraJson,
  IrExtraKeyframe,
  IrLayer,
  IrLine,
  IrNote,
  IrVarValue,
  IrVideo,
} from '../../core/ir/types';
import { jsonEquals } from '../../core/util/json';
import type { Json, Passthrough } from '../../core/util/types';
import type { JsonNode } from '../json/types';
import type {
  RpeBpmItem,
  RpeChart,
  RpeControlPoint,
  RpeDecoded,
  RpeEvent,
  RpeEventLayer,
  RpeExtended,
  RpeJudgeLine,
  RpeNote,
  RpeTriple,
} from '../rpe/json';
import { CONTROL_CURVES, EXTENDED_LIST_KEYS, fieldSpec, isKnownKey, LAYER_LIST_KEYS } from '../rpe/schema';
import type { ControlCurveKey, RpeObjectKind } from '../rpe/schema';

/** Bounded collector of differences. */
export class Diffs {
  readonly list: string[] = [];
  readonly max: number;
  total = 0;
  constructor(max = 50) {
    this.max = max;
  }
  add(path: string, message: string): void {
    this.total++;
    if (this.list.length < this.max) this.list.push(`${path === '' ? '(root)' : path}: ${message}`);
  }
  get empty(): boolean {
    return this.total === 0;
  }
}

function show(v: unknown): string {
  if (v === undefined) return '(absent)';
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? `${s.slice(0, 77)}...` : s;
  } catch {
    return '(unprintable)';
  }
}

function f32eq(a: number, b: number): boolean {
  return Math.fround(a) === Math.fround(b) || (Number.isNaN(a) && Number.isNaN(b));
}

/** Which occurrence of a duplicated passthrough key a writer keeps (RPE writer: last; extra.json writer: first). */
type KeepRule = 'first' | 'last';

const EXTRA_EFFECT_KEYS = new Set(['start', 'end', 'shader', 'vars', 'global']);
const EXTRA_VIDEO_KEYS = new Set(['path', 'time', 'scale', 'alpha', 'dim', 'attach']);
const EXTRA_ROOT_KEYS = new Set(['bpm', 'effects', 'videos']);

class Cmp {
  readonly d: Diffs;
  constructor(d: Diffs) {
    this.d = d;
  }

  eq(path: string, expected: unknown, found: unknown): void {
    if (expected !== found) this.d.add(path, `expected ${show(expected)}, found ${show(found)}`);
  }

  f32(path: string, expected: number, found: number | undefined): void {
    if (found === undefined || !f32eq(expected, found)) {
      this.d.add(path, `expected ${show(expected)} (f32), found ${show(found)}`);
    }
  }

  triple(path: string, expected: RpeTriple, found: RpeTriple | undefined): void {
    if (found?.length !== 3 || found[0] !== expected[0] || found[1] !== expected[1] || found[2] !== expected[2]) {
      this.d.add(path, `expected ${show(expected)}, found ${show(found)}`);
    }
  }

  rgb(path: string, expected: readonly number[] | undefined, found: readonly number[] | null | undefined): void {
    const same =
      expected === undefined
        ? found === undefined
        : found !== undefined &&
          found !== null &&
          found.length === expected.length &&
          found.every((x, i) => x === expected[i]);
    if (!same) this.d.add(path, `expected ${show(expected)}, found ${show(found)}`);
  }

  json(path: string, expected: Json | undefined, found: Json | undefined): void {
    const same = expected === undefined || found === undefined ? expected === found : jsonEquals(expected, found);
    if (!same) this.d.add(path, `expected ${show(expected)}, found ${show(found)}`);
  }

  /** Passthrough of an RPE object as written: last occurrence of a key, at its own position, known keys skipped. */
  passthrough(path: string, kind: RpeObjectKind, expected: Passthrough, found: Passthrough): void {
    this.passthroughWith(path, (k) => isKnownKey(kind, k), 'last', expected, found);
  }

  passthroughWith(
    path: string,
    known: (key: string) => boolean,
    keep: KeepRule,
    expected: Passthrough,
    found: Passthrough,
  ): void {
    const at = new Map<string, number>();
    expected.forEach(([k], i) => {
      if (keep === 'last' || !at.has(k)) at.set(k, i);
    });
    const written = expected.filter(([k], i) => at.get(k) === i && !known(k));
    if (written.length !== found.length) {
      this.d.add(
        `${path}.(unknown keys)`,
        `expected ${show(written.map(([k]) => k))}, found ${show(found.map(([k]) => k))}`,
      );
      return;
    }
    written.forEach(([k, v], i) => {
      const f = found[i];
      if (f?.[0] !== k) this.d.add(`${path}.${k}`, `unknown key order differs (found ${show(f?.[0])})`);
      else this.json(`${path}.${k}`, v, f[1]);
    });
  }
}

// ---------------------------------------------------------------------------------------------------------------
// RPE chart
// ---------------------------------------------------------------------------------------------------------------

type EventValueKind = 'f32' | 'rgb' | 'string';

function compareEvent(c: Cmp, path: string, kind: EventValueKind, e: IrEvent<unknown>, f: RpeEvent<unknown>): void {
  c.triple(`${path}.startTime`, e.startTime, f.startTime);
  c.triple(`${path}.endTime`, e.endTime, f.endTime);
  for (const key of ['start', 'end'] as const) {
    const ev = e[key];
    const fv = f[key];
    if (kind === 'f32') c.f32(`${path}.${key}`, ev as number, typeof fv === 'number' ? fv : undefined);
    else if (kind === 'rgb') c.rgb(`${path}.${key}`, ev as readonly number[], fv as readonly number[] | undefined);
    else c.eq(`${path}.${key}`, ev, fv);
  }
  c.eq(`${path}.easingType`, e.easingType, f.easingType);
  c.f32(`${path}.easingLeft`, e.easingLeft, f.easingLeft);
  c.f32(`${path}.easingRight`, e.easingRight, f.easingRight);
  c.eq(`${path}.bezier`, e.bezier, f.bezier);
  if (f.bezierPoints === undefined) c.eq(`${path}.bezierPoints`, e.bezierPoints, undefined);
  else {
    e.bezierPoints.forEach((p, i) => {
      c.f32(`${path}.bezierPoints[${String(i)}]`, p, f.bezierPoints?.[i]);
    });
  }
  c.eq(`${path}.linkgroup`, e.linkgroup, f.linkgroup);
  c.passthrough(path, 'event', e.extra, f.extra);
}

function compareList(
  c: Cmp,
  path: string,
  kind: EventValueKind,
  expected: readonly IrEvent<unknown>[] | undefined,
  nullKey: boolean,
  found: readonly RpeEvent<unknown>[] | null | undefined,
): number {
  if (expected === undefined) {
    if (nullKey) {
      if (found !== null) c.d.add(path, `expected null, found ${show(found)}`);
    } else if (found !== undefined) c.d.add(path, `expected (absent), found ${found === null ? 'null' : 'a list'}`);
    return 0;
  }
  if (found === null || found === undefined) {
    c.d.add(path, `expected ${String(expected.length)} events, found ${show(found)}`);
    return 0;
  }
  if (found.length !== expected.length) {
    c.d.add(path, `expected ${String(expected.length)} events, found ${String(found.length)}`);
    return 0;
  }
  expected.forEach((e, i) => {
    const f = found[i];
    if (f !== undefined) compareEvent(c, `${path}[${String(i)}]`, kind, e, f);
  });
  return expected.length;
}

function valueKindOf(key: string): EventValueKind {
  if (key === 'colorEvents') return 'rgb';
  if (key === 'textEvents') return 'string';
  return 'f32';
}

/** A raw event list kept verbatim (IrExtended.ignoredLists) against its decoded form. */
function compareRawList(c: Cmp, path: string, raw: Json, found: readonly RpeEvent<unknown>[] | null | undefined): void {
  if (!Array.isArray(raw) || found === null || found === undefined || found.length !== raw.length) {
    c.d.add(path, `kept list differs: expected ${show(raw)}, found ${show(found)}`);
    return;
  }
  (raw as readonly Json[]).forEach((item, i) => {
    const f = found[i];
    const p = `${path}[${String(i)}]`;
    if (item === null || typeof item !== 'object' || Array.isArray(item) || f === undefined) {
      c.d.add(p, 'kept event differs');
      return;
    }
    const o = item as { readonly [k: string]: Json };
    const fo = f as unknown as Readonly<Record<string, unknown>>;
    for (const [k, v] of Object.entries(o)) {
      if (!isKnownKey('event', k)) continue;
      const fv = fo[k];
      if (typeof v === 'number' && typeof fv === 'number') {
        const spec = fieldSpec('event', k);
        if (spec?.kind === 'f32' || k === 'start' || k === 'end') c.f32(`${p}.${k}`, v, fv);
        else c.eq(`${p}.${k}`, v, fv);
      } else c.json(`${p}.${k}`, v, fv as Json | undefined);
    }
    const extra: Passthrough = Object.entries(o).filter(([k]) => !isKnownKey('event', k));
    c.passthrough(p, 'event', extra, f.extra);
  });
}

function compareLayer(c: Cmp, path: string, e: IrLayer, f: RpeEventLayer): number {
  let n = 0;
  for (const key of LAYER_LIST_KEYS) {
    n += compareList(c, `${path}.${key}`, 'f32', e[key], e.nullKeys.includes(key), f[key]);
  }
  c.passthrough(path, 'layer', e.extra, f.extra);
  return n;
}

function compareExtended(c: Cmp, path: string, e: IrExtended, f: RpeExtended): number {
  let n = 0;
  for (const key of EXTENDED_LIST_KEYS) {
    const raw = e.ignoredLists.find(([k]) => k === key);
    if (raw !== undefined) {
      compareRawList(c, `${path}.${key}`, raw[1], f[key]);
      continue;
    }
    const list = e[key] as readonly IrEvent<unknown>[] | undefined;
    n += compareList(c, `${path}.${key}`, valueKindOf(key), list, e.nullKeys.includes(key), f[key]);
  }
  c.passthrough(path, 'extended', e.extra, f.extra);
  return n;
}

function compareNote(c: Cmp, path: string, e: IrNote, f: RpeNote): void {
  c.eq(`${path}.type`, e.type, f.type);
  c.triple(`${path}.startTime`, e.startTime, f.startTime);
  c.triple(`${path}.endTime`, e.endTime, f.endTime);
  c.f32(`${path}.positionX`, e.positionX, f.positionX);
  c.eq(`${path}.above`, e.above, f.above);
  c.eq(`${path}.isFake`, e.isFake, f.isFake);
  c.f32(`${path}.speed`, e.speed, f.speed);
  c.f32(`${path}.yOffset`, e.yOffset, f.yOffset);
  c.f32(`${path}.size`, e.size, f.size);
  c.eq(`${path}.alpha`, e.alpha, f.alpha);
  c.eq(`${path}.visibleTime`, e.visibleTime, f.visibleTime);
  c.eq(`${path}.hitsound`, e.hitsound, f.hitsound);
  if (e.judgeArea === undefined) c.eq(`${path}.judgeArea`, undefined, f.judgeArea);
  else c.f32(`${path}.judgeArea`, e.judgeArea, f.judgeArea ?? undefined);
  c.rgb(`${path}.tint`, e.tint, f.tint);
  c.rgb(`${path}.tintHitEffects`, e.tintHitEffects, f.tintHitEffects);
  c.passthrough(path, 'note', e.extra, f.extra);
}

function compareControls(
  c: Cmp,
  path: string,
  e: readonly IrControlPoint[] | undefined,
  f: readonly RpeControlPoint[] | undefined,
): void {
  if (e === undefined || f === undefined) {
    if (e !== f) c.d.add(path, `expected ${show(e)}, found ${show(f)}`);
    return;
  }
  if (e.length !== f.length) {
    c.d.add(path, `expected ${String(e.length)} points, found ${String(f.length)}`);
    return;
  }
  e.forEach((p, i) => {
    const q = f[i];
    const pp = `${path}[${String(i)}]`;
    if (q === undefined) return;
    c.eq(`${pp}.x`, p.x, q.x);
    c.eq(`${pp}.easing`, p.easing, q.easing);
    c.f32(`${pp}.value`, p.value, q.value);
    const seen = new Set<string>();
    const expectedExtra = p.extra.filter(([k]) => {
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (expectedExtra.length !== q.extra.length) {
      c.d.add(`${pp}.(other keys)`, `expected ${show(expectedExtra)}, found ${show(q.extra)}`);
      return;
    }
    expectedExtra.forEach(([k, v], j) => {
      const fx = q.extra[j];
      if (fx?.[0] !== k || typeof fx[1] !== 'number') c.d.add(`${pp}.${k}`, `expected ${show(v)}, found ${show(fx)}`);
      else c.f32(`${pp}.${k}`, v, fx[1]);
    });
  });
}

function compareLine(c: Cmp, path: string, e: IrLine, f: RpeJudgeLine): { events: number; notes: number } {
  let events = 0;
  c.eq(`${path}.Name`, e.Name, f.Name);
  c.eq(`${path}.Texture`, e.Texture, f.Texture);
  c.eq(`${path}.father`, e.father, f.father);
  c.eq(`${path}.rotateWithFather`, e.rotateWithFather, f.rotateWithFather);
  c.eq(`${path}.isCover`, e.isCover, f.isCover);
  c.eq(`${path}.zOrder`, e.zOrder, f.zOrder);
  c.eq(`${path}.attachUI`, e.attachUI ?? undefined, f.attachUI);
  c.eq(`${path}.Group`, e.Group, f.Group);
  c.eq(`${path}.bpmfactor`, e.bpmfactor, f.bpmfactor);
  c.rgb(`${path}.anchor`, e.anchor, f.anchor);
  c.eq(`${path}.isGif`, e.isGif, f.isGif);
  c.eq(`${path}.numOfNotes`, e.numOfNotes, f.numOfNotes);
  if (e.eventLayers.length !== f.eventLayers.length) {
    c.d.add(
      `${path}.eventLayers`,
      `expected ${String(e.eventLayers.length)} layers, found ${String(f.eventLayers.length)}`,
    );
  } else {
    e.eventLayers.forEach((layer, i) => {
      const fl = f.eventLayers[i];
      const lp = `${path}.eventLayers[${String(i)}]`;
      if (layer === null || fl === null || fl === undefined)
        c.eq(lp, layer === null ? null : 'a layer', fl === null ? null : 'a layer');
      else events += compareLayer(c, lp, layer, fl);
    });
  }
  const ext = e.extended;
  if (ext === 'absent') c.eq(`${path}.extended`, undefined, f.extended === undefined ? undefined : 'present');
  else if (ext === null) c.eq(`${path}.extended`, null, f.extended === null ? null : show(f.extended));
  else if (f.extended === null || f.extended === undefined)
    c.d.add(`${path}.extended`, `expected an object, found ${show(f.extended)}`);
  else events += compareExtended(c, `${path}.extended`, ext, f.extended);
  const notesWritten = e.notesKey === 'present' || e.notes.length > 0;
  let notes = 0;
  if (!notesWritten) c.eq(`${path}.notes`, undefined, f.notes === undefined ? undefined : 'present');
  else if (f.notes === null || f.notes === undefined)
    c.d.add(`${path}.notes`, `expected a list, found ${show(f.notes)}`);
  else if (f.notes.length !== e.notes.length) {
    c.d.add(`${path}.notes`, `expected ${String(e.notes.length)} notes, found ${String(f.notes.length)}`);
  } else {
    e.notes.forEach((n, i) => {
      const fn = f.notes?.[i];
      if (fn !== undefined) compareNote(c, `${path}.notes[${String(i)}]`, n, fn);
    });
    notes = e.notes.length;
  }
  for (const key of Object.keys(CONTROL_CURVES) as ControlCurveKey[]) {
    compareControls(c, `${path}.${key}`, e[key], f[key]);
  }
  c.json(`${path}.skewControl`, e.skewControl, f.skewControl);
  c.passthrough(path, 'line', e.extra, f.extra);
  return { events, notes };
}

function compareBpm(c: Cmp, e: readonly IrBpm[], f: readonly RpeBpmItem[]): void {
  if (e.length !== f.length) {
    c.d.add('BPMList', `expected ${String(e.length)} points, found ${String(f.length)}`);
    return;
  }
  e.forEach((b, i) => {
    const fb = f[i];
    const p = `BPMList[${String(i)}]`;
    if (fb === undefined) return;
    c.eq(`${p}.bpm`, b.bpm, fb.bpm);
    c.triple(`${p}.startTime`, b.startTime, fb.startTime);
    c.passthrough(p, 'bpm', b.extra, fb.extra);
  });
}

export interface ChartCompareCounts {
  readonly lines: number;
  readonly events: number;
  readonly notes: number;
}

/** Compares a decoded chart (before normalisation) with the IR that was written. */
export function compareChart(decoded: RpeDecoded, ir: IrChart, diffs: Diffs): ChartCompareCounts {
  const c = new Cmp(diffs);
  const f: RpeChart = decoded.chart;
  const m = ir.META;
  if (typeof m.RPEVersion === 'number') {
    c.eq('META.RPEVersion', m.RPEVersion, f.META.RPEVersion);
    c.eq('META.RPEVersion (lexeme)', null, decoded.rpeVersionRaw === null ? null : decoded.rpeVersionRaw.lexeme);
  } else {
    c.eq('META.RPEVersion (kind)', m.RPEVersion.kind, decoded.rpeVersionRaw?.kind);
    c.eq('META.RPEVersion (lexeme)', m.RPEVersion.lexeme, decoded.rpeVersionRaw?.lexeme);
  }
  c.eq('META.RPEVersion (effective)', m.rpeVersionEffective, decoded.rpeVersionEffective);
  c.eq('META.offset', m.offset, f.META.offset);
  for (const key of ['name', 'level', 'charter', 'composer', 'illustration', 'background', 'song', 'id'] as const) {
    c.eq(`META.${key}`, m[key], f.META[key]);
  }
  c.passthrough('META', 'meta', m.extra, f.META.extra);
  compareBpm(c, ir.BPMList, f.BPMList);
  c.json(
    'judgeLineGroup',
    ir.judgeLineGroup.length === 0 ? undefined : [...ir.judgeLineGroup],
    f.judgeLineGroup === undefined ? undefined : [...f.judgeLineGroup],
  );
  if (ir.timeTags === undefined || f.timeTags === undefined)
    c.eq('timeTags', ir.timeTags === undefined, f.timeTags === undefined);
  else if (ir.timeTags.length !== f.timeTags.length) c.d.add('timeTags', 'different number of time tags');
  else {
    ir.timeTags.forEach((t, i) => {
      const ft = f.timeTags?.[i];
      c.eq(`timeTags[${String(i)}].name`, t.name, ft?.name);
      c.triple(`timeTags[${String(i)}].time`, t.time, ft?.time);
    });
  }
  c.eq('multiLineString', ir.multiLineString, f.multiLineString);
  c.eq('multiScale', ir.multiScale, f.multiScale);
  c.eq('chartTime', ir.chartTime, f.chartTime);
  c.eq('xybind', ir.xybind, f.xybind);
  c.passthrough('', 'chart', ir.rootExtra, f.extra);
  let events = 0;
  let notes = 0;
  if (ir.judgeLineList.length !== f.judgeLineList.length) {
    c.d.add(
      'judgeLineList',
      `expected ${String(ir.judgeLineList.length)} lines, found ${String(f.judgeLineList.length)}`,
    );
  } else {
    ir.judgeLineList.forEach((line, i) => {
      const fl = f.judgeLineList[i];
      if (fl === undefined) return;
      const r = compareLine(c, `judgeLineList[${String(i)}]`, line, fl);
      events += r.events;
      notes += r.notes;
    });
  }
  return { lines: ir.judgeLineList.length, events, notes };
}

// ---------------------------------------------------------------------------------------------------------------
// Integer typing on the lossless tree
// ---------------------------------------------------------------------------------------------------------------

const INT_KINDS: ReadonlySet<string> = new Set(['i32', 'u8', 'u16', 'u32']);

function member(node: JsonNode, key: string): JsonNode | undefined {
  if (node.k !== 'obj') return undefined;
  let found: JsonNode | undefined;
  for (const [k, v] of node.entries) if (k === key) found = v;
  return found;
}

function checkObject(node: JsonNode, kind: RpeObjectKind, path: string, d: Diffs): void {
  if (node.k !== 'obj') return;
  for (const [key, value] of node.entries) {
    const spec = fieldSpec(kind, key);
    if (spec === undefined) continue;
    const p = `${path}${path === '' ? '' : '.'}${key}`;
    if (INT_KINDS.has(spec.kind) && value.k === 'num' && !value.int) d.add(p, `integer field written as ${value.lex}`);
    if (spec.kind === 'triple' || spec.kind === 'rgb') {
      if (value.k === 'arr') {
        value.items.forEach((item, i) => {
          if (item.k === 'num' && !item.int) d.add(`${p}[${String(i)}]`, `integer written as ${item.lex}`);
        });
      }
    }
  }
}

function checkEventLists(node: JsonNode | undefined, keys: readonly string[], path: string, d: Diffs): void {
  if (node?.k !== 'obj') return;
  for (const key of keys) {
    const list = member(node, key);
    if (list?.k !== 'arr') continue;
    list.items.forEach((ev, i) => {
      checkObject(ev, 'event', `${path}.${key}[${String(i)}]`, d);
    });
  }
}

/** Integer typing of every schema field of a written chart (the lexer's int flag). */
export function checkIntegerTyping(root: JsonNode, rpeVersionIsInt: boolean, d: Diffs): void {
  checkObject(root, 'chart', '', d);
  const meta = member(root, 'META');
  if (meta !== undefined) {
    checkObject(meta, 'meta', 'META', d);
    const ver = member(meta, 'RPEVersion');
    if (rpeVersionIsInt && ver?.k === 'num' && !ver.int) d.add('META.RPEVersion', `integer written as ${ver.lex}`);
  }
  const bpm = member(root, 'BPMList');
  if (bpm?.k === 'arr') {
    bpm.items.forEach((b, i) => {
      checkObject(b, 'bpm', `BPMList[${String(i)}]`, d);
    });
  }
  const lines = member(root, 'judgeLineList');
  if (lines?.k !== 'arr') return;
  lines.items.forEach((line, li) => {
    const path = `judgeLineList[${String(li)}]`;
    checkObject(line, 'line', path, d);
    const layers = member(line, 'eventLayers');
    if (layers?.k === 'arr') {
      layers.items.forEach((layer, i) => {
        checkEventLists(layer, LAYER_LIST_KEYS, `${path}.eventLayers[${String(i)}]`, d);
      });
    }
    checkEventLists(member(line, 'extended'), EXTENDED_LIST_KEYS, `${path}.extended`, d);
    const notes = member(line, 'notes');
    if (notes?.k === 'arr') {
      notes.items.forEach((n, i) => {
        checkObject(n, 'note', `${path}.notes[${String(i)}]`, d);
      });
    }
    for (const curve of Object.keys(CONTROL_CURVES)) {
      const pts = member(line, curve);
      if (pts?.k === 'arr') {
        pts.items.forEach((p, i) => {
          checkObject(p, 'control', `${path}.${curve}[${String(i)}]`, d);
        });
      }
    }
  });
}

// ---------------------------------------------------------------------------------------------------------------
// extra.json
// ---------------------------------------------------------------------------------------------------------------

type KeyframeValue = number | readonly number[];

function compareKeyframes(
  c: Cmp,
  path: string,
  e: readonly IrExtraKeyframe<KeyframeValue>[],
  f: readonly IrExtraKeyframe<KeyframeValue>[],
): void {
  if (e.length !== f.length) {
    c.d.add(path, `expected ${String(e.length)} keyframes, found ${String(f.length)}`);
    return;
  }
  e.forEach((k, i) => {
    const q = f[i];
    const p = `${path}[${String(i)}]`;
    if (q === undefined) return;
    c.triple(`${p}.startTime`, k.startTime, q.startTime);
    c.triple(`${p}.endTime`, k.endTime, q.endTime);
    for (const key of ['start', 'end'] as const) compareVarFixed(c, `${p}.${key}`, k[key], q[key]);
    c.eq(`${p}.easingType`, k.easingType, q.easingType);
    if (k.easingLeft === undefined) c.eq(`${p}.easingLeft`, undefined, q.easingLeft);
    else c.f32(`${p}.easingLeft`, k.easingLeft, q.easingLeft);
    if (k.easingRight === undefined) c.eq(`${p}.easingRight`, undefined, q.easingRight);
    else c.f32(`${p}.easingRight`, k.easingRight, q.easingRight);
  });
}

function compareVarFixed(c: Cmp, path: string, e: KeyframeValue, f: KeyframeValue): void {
  if (typeof e === 'number') {
    c.f32(path, e, typeof f === 'number' ? f : undefined);
    return;
  }
  if (typeof f === 'number' || f.length !== e.length) {
    c.d.add(path, `expected ${show(e)}, found ${show(f)}`);
    return;
  }
  // Colours (4 components) are integers, vec2 components f32.
  e.forEach((x, i) => {
    if (e.length === 4) c.eq(`${path}[${String(i)}]`, x, f[i]);
    else c.f32(`${path}[${String(i)}]`, x, f[i]);
  });
}

function compareVar(c: Cmp, path: string, e: IrVarValue | undefined, f: IrVarValue | undefined): void {
  if (e === undefined || f === undefined || e === null || f === null) {
    if (e !== f) c.d.add(path, `expected ${show(e)}, found ${show(f)}`);
    return;
  }
  const eList = Array.isArray(e) && e.length > 0 && typeof e[0] === 'object';
  const fList = Array.isArray(f) && f.length > 0 && typeof f[0] === 'object';
  if (eList !== fList) {
    c.d.add(path, `expected ${show(e)}, found ${show(f)}`);
    return;
  }
  if (eList) {
    compareKeyframes(
      c,
      path,
      e as readonly IrExtraKeyframe<KeyframeValue>[],
      f as readonly IrExtraKeyframe<KeyframeValue>[],
    );
  } else compareVarFixed(c, path, e as KeyframeValue, f as KeyframeValue);
}

function compareEffect(c: Cmp, path: string, e: IrEffect, f: IrEffect): void {
  c.triple(`${path}.start`, e.start, f.start);
  c.triple(`${path}.end`, e.end, f.end);
  c.eq(`${path}.shader`, e.shader, f.shader);
  c.eq(`${path}.global`, e.global, f.global);
  if (e.vars.length !== f.vars.length)
    c.d.add(`${path}.vars`, `expected ${String(e.vars.length)} variables, found ${String(f.vars.length)}`);
  else {
    e.vars.forEach(([name, v], i) => {
      const fv = f.vars[i];
      if (fv?.[0] !== name) c.d.add(`${path}.vars`, `variable order differs at ${name}`);
      else compareVar(c, `${path}.vars.${name}`, v, fv[1]);
    });
  }
  c.passthroughWith(path, (k) => EXTRA_EFFECT_KEYS.has(k), 'first', e.extra, f.extra);
}

function compareVideo(c: Cmp, path: string, e: IrVideo, f: IrVideo): void {
  c.eq(`${path}.path`, e.path, f.path);
  c.triple(`${path}.time`, e.time, f.time);
  c.eq(`${path}.scale`, e.scale, f.scale);
  compareVar(c, `${path}.alpha`, e.alpha, f.alpha);
  compareVar(c, `${path}.dim`, e.dim, f.dim);
  c.eq(`${path}.attach`, e.attach?.line, f.attach?.line);
  c.passthroughWith(path, (k) => EXTRA_VIDEO_KEYS.has(k), 'first', e.extra, f.extra);
}

/** Compares decoded extra.json IR with the IR that was written (source ids ignored). */
export function compareExtra(expected: IrExtraJson, found: IrExtraJson, diffs: Diffs): void {
  const c = new Cmp(diffs);
  if (expected.bpm.length !== found.bpm.length) c.d.add('extra.json bpm', 'different number of BPM points');
  else {
    expected.bpm.forEach((b, i) => {
      const fb = found.bpm[i];
      c.f32(`extra.json bpm[${String(i)}].bpm`, b.bpm, fb?.bpm);
      c.triple(`extra.json bpm[${String(i)}].time`, b.time, fb?.time);
    });
  }
  if (expected.effects.length !== found.effects.length) {
    c.d.add('extra.json effects', `expected ${String(expected.effects.length)}, found ${String(found.effects.length)}`);
  } else {
    expected.effects.forEach((e, i) => {
      const f = found.effects[i];
      if (f !== undefined) compareEffect(c, `extra.json effects[${String(i)}]`, e, f);
    });
  }
  if (expected.videos.length !== found.videos.length) {
    c.d.add('extra.json videos', `expected ${String(expected.videos.length)}, found ${String(found.videos.length)}`);
  } else {
    expected.videos.forEach((v, i) => {
      const f = found.videos[i];
      if (f !== undefined) compareVideo(c, `extra.json videos[${String(i)}]`, v, f);
    });
  }
  c.passthroughWith('extra.json', (k) => EXTRA_ROOT_KEYS.has(k), 'first', expected.rootExtra, found.rootExtra);
}
